import React, { useEffect, useState } from "react";
import { Offcanvas, Button, Spinner } from "react-bootstrap";
import instance from "../../services/baseService";
import { toast } from "react-hot-toast";
import { ROUTES } from "../../constants/roles";
import { useDispatch, useSelector } from 'react-redux';
import { getRoles } from '../../slices/userReducer';
import { FaTrash } from 'react-icons/fa';
import { truncateText } from "../../utils";
import { extractErrorMessage } from "../../constants/common";
import DynamicModal from "../Employees/DynamicModal";

const DeleteRoles = ({ show, onHide, onRoleDeleted }) => {
    const [deletingId, setDeletingId] = useState(null);
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        message: "",
        onConfirm: null
    });

    const dispatch = useDispatch();
    const { roles, loading } = useSelector((state) => state.user);

    useEffect(() => {
        if (show) {
            dispatch(getRoles());
        }
    }, [dispatch, show]);

    const triggerDelete = (id, roleName) => {
        setConfirmModal({
            show: true,
            message: `Are you sure you want to delete the role "${roleName}"?`,
            onConfirm: () => confirmDelete(id)
        });
    };

    const confirmDelete = async (id) => {
        setDeletingId(id);
        try {
            const response = await instance.post(`/api/v1/rbac/roles/delete/${id}`);
            console.log(response);

            if (response?.data?.type === "success" || response?.status === 200) {
                toast.success(response?.data?.message || "Role Deleted");
                dispatch(getRoles());
                if (onRoleDeleted) onRoleDeleted();
            }
        } catch (err) {
            console.error("Delete error:", err);
            const errorMessage = extractErrorMessage(err);
            toast.error(errorMessage);
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <>
            <Offcanvas show={show} onHide={onHide} placement="end" style={{ maxWidth: "600px" }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="h4 text-dark">Delete Roles</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {loading ? (
                        <div className="d-flex justify-content-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : roles.length > 0 ? (
                        <ul className="space-y-3 p-0">
                            {roles.map((role, index) => (
                                <li key={role.id} className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <div className="d-flex align-items-center">
                                        <span className="text-muted me-2 fw-bold">{index + 1}.</span>
                                        <span className="text-dark fw-medium">{truncateText(role.name, 30)}</span>
                                    </div>
                                    <Button 
                                        variant="link" 
                                        className="text-danger" 
                                        onClick={() => triggerDelete(role.id, role.name)}
                                        disabled={deletingId === role.id}
                                    >
                                        {deletingId === role.id ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <FaTrash />
                                        )}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-4 text-muted">No roles found.</div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            <DynamicModal
                show={confirmModal.show}
                onHide={() => setConfirmModal({ ...confirmModal, show: false })}
                title="Confirm Delete"
                buttons={[
                    { 
                        label: 'Cancel', 
                        variant: 'secondary', 
                        action: 'custom', 
                        onClick: () => setConfirmModal({ ...confirmModal, show: false }) 
                    },
                    { 
                        label: 'Delete', 
                        variant: 'danger', 
                        action: 'custom', 
                        onClick: async () => { 
                            setConfirmModal({ ...confirmModal, show: false }); 
                            if (confirmModal.onConfirm) await confirmModal.onConfirm(); 
                        } 
                    }
                ]}
            >
                <p>{confirmModal.message}</p>
            </DynamicModal>
        </>
    );
};

export default DeleteRoles;